import React from 'react';
import PropTypes from 'prop-types';
import { CONVERTTIME } from '../../../../utils/dateController';
import '../../../../css/registrationList.css';

function getArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeID(value) {
  if (!value) return '';
  if (value._id) return value._id.toString();
  return value.toString();
}

function getMembershipData(registration) {
  if (!registration || !registration.membershipData) return {};
  if (Array.isArray(registration.membershipData)) {
    return registration.membershipData[0] || {};
  }
  return registration.membershipData;
}

function getWaitingListData(registration, programWaitingList) {
  const waitingListData = getArray(registration.waitingListData);
  if (waitingListData.length > 0) return waitingListData;

  return programWaitingList.map((waitingListID) => ({
    _id: waitingListID,
    familyMembers: [],
  }));
}

function getWaitlistedFamilyMemberIDs(registration, waitingListData) {
  const familyMemberIDs = waitingListData.reduce((ids, waitingList) => {
    getArray(waitingList.familyMembers).forEach((familyMemberID) => {
      const normalizedID = normalizeID(familyMemberID);
      if (normalizedID) ids.add(normalizedID);
    });
    return ids;
  }, new Set());

  if (familyMemberIDs.size > 0) return Array.from(familyMemberIDs);

  return getArray(registration.familyMembers).map(normalizeID).filter(Boolean);
}

function getWaitlistedFamilyMembers(registration, membershipData, waitingListData) {
  const waitlistedFamilyMemberIDs = getWaitlistedFamilyMemberIDs(
    registration,
    waitingListData,
  );
  const candidates = [
    ...getArray(registration.waitingListFamilyMembersData),
    ...getArray(registration.familyMembersData),
    ...getArray(membershipData.familyMembersInformation),
  ];
  const familyMembersByID = candidates.reduce((familyMembers, familyMember) => {
    const familyMemberID = normalizeID(familyMember);
    if (familyMemberID && !familyMembers[familyMemberID]) {
      familyMembers[familyMemberID] = familyMember;
    }
    return familyMembers;
  }, {});

  if (waitlistedFamilyMemberIDs.length > 0) {
    return waitlistedFamilyMemberIDs.map((familyMemberID) => (
      familyMembersByID[familyMemberID] || { _id: familyMemberID }
    ));
  }

  return candidates;
}

function getWaitingListRow(registration) {
  if (!registration) return null;

  const programWaitingList = getArray(registration.programWaitingList);
  if (programWaitingList.length < 1) return null;

  const membershipData = getMembershipData(registration);
  const waitingListData = getWaitingListData(registration, programWaitingList);
  const familyMembersData = getWaitlistedFamilyMembers(
    registration,
    membershipData,
    waitingListData,
  );
  const displayMember = familyMembersData.find(
    (familyMember) => familyMember.firstName || familyMember.lastName,
  ) || {};

  return {
    membershipID: registration.membershipID || membershipData.membershipID || 'N/A',
    firstName: displayMember.firstName || 'Unknown',
    lastName: displayMember.lastName || 'Member',
    email: membershipData.email || 'N/A',
    phone: displayMember.phone || 'N/A',
    registrationDate: registration.registrationDate
      ? CONVERTTIME(registration.registrationDate, true)
      : 'N/A',
    paymentStatus: registration.paymentStatus || 'Pending',
    id: programWaitingList,
    numberOfAttendees: familyMembersData.length,
    membershipData,
    familyMembersData,
    waitingListData,
    programRegistrationID: registration._id,
  };
}

function rowMatchesSearch(row, sortSearch) {
  if (!sortSearch) return true;
  const searchValue = sortSearch.toLowerCase();
  return [row.membershipID, row.firstName, row.lastName, row.email]
    .some((value) => String(value || '').toLowerCase().includes(searchValue));
}

function rowMatchesStatus(row, sortStatus) {
  if (!Array.isArray(sortStatus) || sortStatus.length < 1) return true;
  return sortStatus.some(
    (status) => String(status || '').toLowerCase()
      === String(row.paymentStatus || '').toLowerCase(),
  );
}

function getWaitingListRows(registrationData, sortStatus, sortSearch) {
  return getArray(registrationData)
    .map(getWaitingListRow)
    .filter((row) => (
      row
      && rowMatchesStatus(row, sortStatus)
      && rowMatchesSearch(row, sortSearch)
    ));
}

export default function WaitingListTableExport({ registrationData, sortStatus, sortSearch }) {
  const rows = getWaitingListRows(registrationData, sortStatus, sortSearch);

  return (
    <div>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Position</th>
            <th scope="col">Membership ID</th>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Phone</th>
            <th scope="col"># of Attendees</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((value, index) => (
            <tr key={value.programRegistrationID || index}>
              <td>{index + 1}</td>
              <td>{value.membershipID}</td>
              <td>
                {value.firstName} {value.lastName}
              </td>
              <td>{value.email}</td>
              <td>{value.phone}</td>
              <td>{value.numberOfAttendees}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

WaitingListTableExport.propTypes = {
  sortStatus: PropTypes.arrayOf(PropTypes.string).isRequired,
  sortSearch: PropTypes.string.isRequired,
  registrationData: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.object),
  ]).isRequired,
};
